<template>
  <article>
    <activity-stage :customRootContainerClass="'dolch-space'">
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article
        slot="content"
        style="display: flex
;
    flex-flow: wrap-reverse;
    justify-content: center;"
        class="dolch-space-content"
      >
        <div v-if="currentRound" class="typing-stage">
          <p class="hidden-input">{{ currentRound.phrase }}</p>
          <div :class="`td-container`">
            <color-block-text class="type-line" :text="currentRound.phrase" :text-step="0">
            </color-block-text>
          </div>
        </div>
        <div v-if="currentRound" :class="{ 'image-stage': true, isMobile }">
          <img
            :alt="currentRound.monochromeImage?.image?.description"
            class="image-before"
            :src="currentRound.monochromeImage?.image?.src"
          />
          <img
            :alt="currentRound.colorImage?.image?.description"
            class="image-after"
            :src="currentRound.colorImage?.image?.src"
          />
        </div>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax } from 'gsap';

import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ActivityManager from 'mixins/ActivityManager';
import AssetLoader from 'mixins/AssetLoader';

import ActivityStage from 'components/activity/ActivityStage.vue';
import ColorBlockText from 'components/text-displays/ColorBlockText.vue';

export default {
  name: 'DolchSpace',

  components: {
    ActivityStage,
    ColorBlockText
  },

  mixins: [AssetLoader, TypingActivityHelper, ActivityManager],

  data() {
    return {
      sfx: {
        soundsPerRounds: null
      }
    };
  },

  beforeDestroy() {
    TweenMax.killAll();
  },

  computed: {
    shouldEndRound() {
      const { currentIndex } = this.typingProgress;
      return currentIndex + 1 === this.currentRound.answer.length;
    }
  },

  methods: {
    async loadActivity() {
      this.createRounds(this.getActivityState());
      const [sounds] = await this.loadAssets(this.loadSounds, this.loadImages);
      this.sfx = sounds;
    },

    getActivityState() {
      const { rounds } = this.activityData;
      return {
        rounds,
        instructions: {
          written: 'Type each phrase.',
          voice: `${this.voiceInstructionsPrefix}/dolchandspacenth-fa-1.mp3`
        }
      };
    },

    async loadImages() {
      this.rounds.forEach(({ monochromeImage, colorImage }) =>
        this.queueImage(monochromeImage.image.src, colorImage.image.src)
      );

      return this.downloadImages();
    },

    async loadSounds() {
      const soundPaths = this.rounds.map(({ answerAudio }) => answerAudio.audio.src);

      this.queueAudio(soundPaths);

      const assetCache = await this.downloadAudio();

      return {
        soundsPerRound: soundPaths.map(path => assetCache.get(path))
      };
    },

    async updateSoundsBasedOnSettings() {
      const [sounds] = await this.loadAssets(this.loadSounds, this.loadImages);
      this.sfx = sounds;
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;
      return {
        keyboardType,
        roofs,
        keyCodesToHandleNatively: [9]
      };
    },

    startRound() {
      this.hideColorImage();
      this.setupNewRound({ stepCallback: this.stepCallback });

      this.$nextTick(this.setActivityContainerHeight);

      this.$nextTick(() => {
        TweenMax.to('.dolch-space', 0.5, {
          autoAlpha: 1,
          onComplete: this.enableKeyboard
        });

        this.setCurrentKeybordingSegment('');
        this.setTotalKeybordingSegment(this.currentRound.answer);
      });
    },

    showColorImage() {
      TweenMax.to('.image-after', 0.5, { autoAlpha: 1 });
    },

    hideColorImage() {
      TweenMax.set('.image-after', { autoAlpha: 0 });
    },

    stepCallback(rootGetters) {
      return rootGetters['activity/currentRound']['answer'];
    },

    handleCorrectKey(attempt) {
      this.sndType();

      this.captureKeyboardAttemptSegment(attempt, this.shouldEndRound);

      if (this.shouldEndRound) {
        this.finishRound();
        return;
      }

      this.incrementTypingIndex();
    },

    sayPhrase() {
      const audio = this.sfx.soundsPerRound[this.progress.roundIndex];

      if (audio && audio.play) {
        audio.play();
      }
    },

    finishRound() {
      const handleNextAction = this.isLastRound ? this.completeActivity : this.startRound;

      this.disableKeyboard();
      this.sndBell();
      this.sayPhrase();
      this.showColorImage();

      TweenMax.to('.dolch-space', 0.5, {
        delay: 1,
        autoAlpha: 0,
        onComplete: handleNextAction
      });
    }
  },
  watch: {
    currentRound() {
      if (this.currentRound?.answer) {
        const answer = this.currentRound.answer;
        const instructionPrefix = `Type the phrase -`;

        if (!answer || `${instructionPrefix} ${answer}` === this.currentScreensReaderAlert) return;
        this.setScreenReaderAlert(`${instructionPrefix} ${answer}`);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.dolch-space-content {
  width: 100%;
  display: flex;

  .typing-stage {
    align-items: center;
    display: flex;
    flex: 1;
    justify-content: center;
    text-align: center;
    vertical-align: top;

    p.tdk2 {
      color: #888888;
    }

    .td-container {
      display: inline-block;
      min-width: 30px;

      .type-line {
        position: relative;
        font-size: 50px;
        font-family: hwtprint;
        height: 160px;

        span {
          display: inline-block;
        }

        .keyStyle {
          padding: 15px 50px 15px 50px;
        }

        &.spacebar {
          background-position: center center;
          margin-right: 130px;
        }
      }
    }
  }

  .image-stage {
    display: flex;
    justify-content: center;
    position: relative;

    img {
      height: auto;
      width: 75%;
    }

    .image-after {
      left: 50%;
      position: absolute;
      top: 0;
      transform: translateX(-50%);
      visibility: hidden;
    }

    &.isMobile {
      img {
        width: 60%;
      }
    }
  }
}
</style>
